/**
 * Web PostHog backend — implements AnalyticsBackend for posthog-js.
 *
 * Migrated from analytics.ts. Keeps web-specific enrichment (referrer
 * classification, UTM parsing, wallet detection, visitor context).
 */

import posthog from "posthog-js";
import type { AnalyticsBackend } from "@orb-labs/orby-react";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_API_KEY as string | undefined;
const POSTHOG_HOST = "https://us.i.posthog.com";

const ATTRIBUTION_STORAGE_KEY = "bullrun_attribution";

// ── Referrer classification ──────────────────────────────────────────

const SOCIAL_DOMAINS: Record<string, string> = {
  "t.co": "twitter",
  "twitter.com": "twitter",
  "x.com": "twitter",
  "discord.com": "discord",
  "telegram.org": "telegram",
  "t.me": "telegram",
  "reddit.com": "reddit",
  "facebook.com": "facebook",
  "fb.com": "facebook",
  "instagram.com": "instagram",
  "youtube.com": "youtube",
  "youtu.be": "youtube",
  "tiktok.com": "tiktok",
  "linkedin.com": "linkedin",
};

const SEARCH_DOMAINS = ["google.com", "google.co", "bing.com", "duckduckgo.com", "yahoo.com", "baidu.com"];

function classifyReferrer(): { referrer_platform: string; referrer_domain: string; referrer_category: string } {
  const { referrer } = document;
  if (!referrer) {
    return { referrer_platform: "direct", referrer_domain: "", referrer_category: "direct" };
  }

  let hostname: string;
  try {
    hostname = new URL(referrer).hostname.replace(/^www\./, "");
  } catch {
    return { referrer_platform: "other", referrer_domain: referrer, referrer_category: "other" };
  }

  for (const [domain, platform] of Object.entries(SOCIAL_DOMAINS)) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return { referrer_platform: platform, referrer_domain: hostname, referrer_category: "social" };
    }
  }

  for (const domain of SEARCH_DOMAINS) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return { referrer_platform: "search", referrer_domain: hostname, referrer_category: "search" };
    }
  }

  return { referrer_platform: "other", referrer_domain: hostname, referrer_category: "other" };
}

// ── Attribution ──────────────────────────────────────────────────────

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const AD_CLICK_PARAMS = ["gclid", "fbclid", "twclid"] as const;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  gclid?: string;
  fbclid?: string;
  twclid?: string;
}

function parseAttribution(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const attribution: Attribution = {};

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) (attribution as Record<string, string>)[key] = value;
  }

  for (const key of AD_CLICK_PARAMS) {
    const value = params.get(key);
    if (value) (attribution as Record<string, string>)[key] = value;
  }

  const ref = params.get("ref") || params.get("referral") || params.get("code");
  if (ref) attribution.ref = ref;

  return attribution;
}

function persistAttribution(attribution: Attribution): void {
  if (Object.keys(attribution).length === 0) return;
  try {
    const existing = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}");
    const merged = { ...existing, ...attribution };
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // localStorage unavailable
  }
}

// ── Visitor context ──────────────────────────────────────────────────

function captureVisitorContext(): Record<string, unknown> {
  const resolved = Intl.DateTimeFormat().resolvedOptions();
  return {
    browser_language: navigator.language,
    browser_languages: navigator.languages || [navigator.language],
    timezone: resolved.timeZone || "unknown",
    intl_locale: resolved.locale || navigator.language,
  };
}

// ── Wallet detection ─────────────────────────────────────────────────

interface EIP6963Detail {
  info: { uuid: string; name: string; icon: string; rdns: string };
  provider: unknown;
}

function detectWallets(): Promise<Record<string, unknown>> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const walletNames: string[] = [];
      const evmDetails: Array<{ name: string; rdns: string }> = [];

      const onAnnounce = (event: Event) => {
        const { detail } = event as CustomEvent<EIP6963Detail>;
        if (detail?.info?.name) {
          evmDetails.push({ name: detail.info.name, rdns: detail.info.rdns || "" });
          if (!walletNames.includes(detail.info.name)) {
            walletNames.push(detail.info.name);
          }
        }
      };
      window.addEventListener("eip6963:announceProvider", onAnnounce);
      window.dispatchEvent(new Event("eip6963:requestProvider"));

      setTimeout(() => {
        window.removeEventListener("eip6963:announceProvider", onAnnounce);

        const w = window as unknown as Record<string, unknown>;
        const eth = w.ethereum as Record<string, unknown> | undefined;
        if (eth && evmDetails.length === 0) {
          let name = "Unknown EVM Wallet";
          if (eth.isMetaMask) name = "MetaMask";
          else if (eth.isCoinbaseWallet) name = "Coinbase Wallet";
          else if (eth.isBraveWallet) name = "Brave Wallet";
          else if (eth.isRabby) name = "Rabby";
          evmDetails.push({ name, rdns: "" });
          if (!walletNames.includes(name)) walletNames.push(name);
        }

        const solana = w.solana as Record<string, unknown> | undefined;
        const phantom = w.phantom as Record<string, unknown> | undefined;
        const hasSolana = !!(solana || phantom?.solana);
        if (hasSolana) {
          const name = (phantom?.solana as Record<string, unknown>)?.isPhantom ? "Phantom" : "Solana Wallet";
          if (!walletNames.includes(name)) walletNames.push(name);
        }

        const hasCosmos = !!w.keplr;
        if (hasCosmos && !walletNames.includes("Keplr")) {
          walletNames.push("Keplr");
        }

        resolve({
          has_wallet: walletNames.length > 0,
          wallet_count: walletNames.length,
          wallet_names: walletNames,
          has_evm_wallet: evmDetails.length > 0,
          has_solana_wallet: hasSolana,
          has_cosmos_wallet: hasCosmos,
          evm_wallet_details: evmDetails,
        });
      }, 100);
    }, 500);
  });
}

// ── AnalyticsBackend implementation ──────────────────────────────────

export function createWebPosthogBackend(): AnalyticsBackend {
  if (!POSTHOG_KEY) {
    console.warn("[observability] VITE_POSTHOG_API_KEY not set — analytics disabled");
    return noopAnalyticsBackend;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    capture_performance: true,
    persistence: "localStorage+cookie",
  });

  // Web-specific enrichment
  const attribution = parseAttribution();
  persistAttribution(attribution);

  if (Object.keys(attribution).length > 0) {
    posthog.register(attribution);
  }
  if (attribution.ref) {
    posthog.setPersonProperties({ referral_code: attribution.ref });
  }

  posthog.setPersonProperties(classifyReferrer());
  posthog.setPersonProperties(captureVisitorContext());

  detectWallets().then((walletInfo) => {
    posthog.setPersonProperties(walletInfo);
    if (walletInfo.has_wallet) {
      posthog.register({ has_wallet: true, wallet_count: walletInfo.wallet_count });
    }
  });

  return {
    capture(event, properties) {
      posthog.capture(event, properties);
    },
    identify(userId, properties) {
      posthog.identify(userId, properties);
    },
    reset() {
      posthog.reset();
    },
    setPersonProperties(properties) {
      posthog.setPersonProperties(properties);
    },
    register(superProperties) {
      posthog.register(superProperties);
    },
    getSessionInfo() {
      return {
        sessionId: posthog.get_session_id() ?? null,
        distinctId: posthog.get_distinct_id() ?? null,
      };
    },
    isFeatureEnabled(key) {
      return posthog.isFeatureEnabled(key) ?? false;
    },
    getAllFlags() {
      const raw = posthog.featureFlags.getFlagVariants();
      const flags: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(raw)) {
        flags[k] = v === true || v === "true";
      }
      return flags;
    },
    async reloadFlags() {
      await posthog.reloadFeatureFlags();
    },
    async shutdown() {
      posthog.reset();
    },
  };
}

const noopAnalyticsBackend: AnalyticsBackend = {
  capture() {},
  identify() {},
  reset() {},
};

// ── Web-specific helpers (used by DeepLinkFallback, AuthModal) ───────

export function persistReferralCode(code: string): void {
  persistAttribution({ ref: code });
  if (POSTHOG_KEY) {
    posthog.register({ ref: code });
    posthog.setPersonProperties({ referral_code: code });
  }
}

/**
 * Persist campaign attribution arriving on a path-style short link (e.g.
 * `/p/ab`) rather than a `?utm_*=` query string. Mirrors the init-time
 * `parseAttribution` → `persistAttribution` → `posthog.register` flow so a
 * later signup attributes to the same UTM fields. Used by the Xreacher
 * outreach short links — see `usePromoOutreachRedirect` and
 * `docs/growth/xreacher-campaign-spec.md`.
 *
 * Call-order assumption: `initObservability()` (which runs `posthog.init`)
 * must have run first. The only caller is `usePromoOutreachRedirect`, mounted
 * in `AppInner` well after init, so this holds. The `POSTHOG_KEY` guard mirrors
 * `persistReferralCode`; localStorage persistence happens regardless of it.
 */
export function persistCampaignAttribution(attribution: Attribution): void {
  if (Object.keys(attribution).length === 0) return;
  persistAttribution(attribution);
  if (POSTHOG_KEY) {
    posthog.register(attribution);
    if (attribution.ref) {
      posthog.setPersonProperties({ referral_code: attribution.ref });
    }
  }
}

export function getStoredAttribution(): Attribution {
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "{}") as Attribution;
  } catch {
    return {};
  }
}

export function captureCTAClick(cta: string, properties?: Record<string, unknown>): void {
  posthog.capture("cta_click", { cta, ...properties });
}
